import type { CSelectItem } from '@/components/shared/CSelect';
import type { ExternalProvider } from '@/typings/auth/externalAuth';

/**
 * The registration endpoint binds StoreType by enum name, not by its numeric value,
 * so these options stay as strings (unlike the admin panel's add-store flow).
 */
export const storeTypeOptions: CSelectItem[] = [
    { value: 'Shopify', label: 'Shopify' },
    { value: 'WooCommerce', label: 'WooCommerce' },
    { value: 'Etsy', label: 'Etsy' },
    { value: 'Amazon', label: 'Amazon' },
    { value: 'Custom', label: 'Custom' },
    { value: 'Shoplazza', label: 'Shoplazza' },
    { value: 'BigCommerce', label: 'BigCommerce' },
    { value: 'Magento', label: 'Magento' },
    { value: 'Walmart', label: 'Walmart' },
    { value: 'Weebly', label: 'Weebly' },
    { value: 'Wix', label: 'Wix' },
    { value: 'Miva', label: 'Miva' }
];

/** Store types that onboard through their own app store instead of this form. */
export const appStoreListings: Record<string, { name: string; url: string }> = {
    Shopify: {
        name: 'Shopify',
        url: 'https://apps.shopify.com/customily-product-personalizer'
    },
    Shoplazza: {
        name: 'Shoplazza',
        url: 'https://appstore.shoplazza.com/listing/139181/customily-product-personalizer/'
    }
};

/** Maps a `?storeType=` value in any casing (e.g. `etsy`) to the enum name the backend binds. */
export const normalizeStoreType = (value: string | null): string | undefined => {
    const lowerValue = value?.trim().toLowerCase();
    const match = storeTypeOptions.find((option) => String(option.value).toLowerCase() === lowerValue);
    return match ? String(match.value) : undefined;
};

export interface StoreDescription {
    title: string;
    summary: string;
    image?: string;
    benefits: string[];
    reviews: Record<'store' | 'country' | 'content' | 'avatar', string>[];
    /** Provider offered next to the email sign up, if any. */
    provider?: ExternalProvider;
}

export const getStoreDescription = (storeType?: string | null): StoreDescription =>
    storeDescription[storeType || ''] || storeDescription.default;

export const storeDescription: Readonly<Record<string, StoreDescription>> = {
    default: {
        title: 'Start selling personalized products on your store and boost your revenue!',
        summary: 'No design skills, no manual order processing, no extra work. Just more profit.',
        provider: 'google',
        benefits: [
            'Sell custom products on any e-commerce platform',
            'Shoppers customize with a live preview before buying',
            'Print-ready files generated automatically with every order',
            '1-on-1 expert onboarding to help you with initial setup',
            'Scale your business effortlessly, live in under 10 minutes'
        ],
        reviews: [
            {
                store: 'One & Only Prints',
                avatar: 'one_and_only_prints.svg',
                country: 'United Kingdom',
                content:
                    "This is the best app for personalizing your products. I've not found another app that comes close."
            },
            {
                store: 'Wunsch Kalender',
                avatar: 'wunsch_kalender.svg',
                country: 'Germany',
                content:
                    'This app is a game-changer and is saving us so many hours of work every single day. The best app for customized products by far!'
            },
            {
                store: 'Incredible Cookies',
                avatar: 'incredible_cookies.svg',
                country: 'United States',
                content:
                    'They are the best and our business has grown far beyond our original expectations. Solid platform and an amazing support team.'
            }
        ]
    },
    Shopify: {
        title: 'The best way to sell personalized products on Shopify. Try it for FREE. Cancel anytime.',
        summary:
            'Let your customers design their own products and get print-ready files automatically. No coding, no manual work.',
        image: 'shopify-logo.svg',
        benefits: [
            'Works with your existing Shopify products, no coding needed',
            'Shoppers customize with a live preview before buying',
            'Print-ready files generated automatically with every order',
            'Free clipart & design libraries included, ready to sell from day one',
            '1-on-1 expert onboarding to get you live fast'
        ],
        reviews: [
            {
                store: 'One & Only Prints',
                avatar: 'one_and_only_prints.svg',
                country: 'United Kingdom',
                content:
                    "This is the best app for personalizing your products. I've not found another app that comes close."
            },
            {
                store: 'Mothers Family Rings',
                avatar: 'mothers_family_rings.svg',
                country: 'United States',
                content:
                    "Since integrating Customily, we've observed a significant increase in conversion rates, and customer feedback has been overwhelmingly positive."
            },
            {
                store: 'Incredible Cookies',
                avatar: 'incredible_cookies.svg',
                country: 'United States',
                content:
                    'They are the best and our business has grown far beyond our original expectations. Solid platform and an amazing support team.'
            }
        ]
    },
    WooCommerce: {
        title: 'Add personalization to your WooCommerce store in under 10 min. Try it for FREE. Cancel anytime.',
        summary:
            'Let your customers design their own products and get print-ready files automatically. No coding, no manual work.',
        image: 'woocommerce-logo.png',
        provider: 'google',
        benefits: [
            'Works with your existing WooCommerce products',
            'Shoppers customize with a live preview before buying',
            'Print-ready files generated automatically per order',
            'Scales with your store with no extra operational work',
            '1-on-1 expert onboarding included'
        ],
        reviews: [
            {
                store: 'Wunsch Kalender',
                avatar: 'wunsch_kalender.svg',
                country: 'Germany',
                content:
                    'This app is a game-changer and is saving us so many hours of work every single day. This is the best app for customized and personalized products by far!'
            },
            {
                store: 'Incredible Cookies',
                avatar: 'incredible_cookies.svg',
                country: 'United States',
                content:
                    'They are the best and our business has grown far beyond our original expectations. Solid platform and an amazing support team.'
            },
            {
                store: 'Happy Paws Pet Candles Co',
                avatar: 'happy_paws.svg',
                country: 'United States',
                content:
                    'Awesome Sauce!!! This app was like finding GOLD! Love it because the possibilities are pretty much endless.'
            }
        ]
    },
    Etsy: {
        title: 'The best way to sell personalized products on Etsy. Try it for FREE. Cancel anytime.',
        summary: 'Simplify your workflow, reduce errors, and automate fulfillment. All from one dashboard.',
        image: 'etsy-logo.png',
        provider: 'etsy',
        benefits: [
            'Works with your existing Etsy listings, no coding needed',
            'Print-ready files generated automatically with every order',
            'Connect with top POD providers like Printify, Printful and more',
            'Free clipart & design libraries included, ready to sell from day one',
            '1-on-1 expert onboarding to get you live fast'
        ],
        reviews: [
            {
                store: 'One & Only Prints',
                avatar: 'one_and_only_prints.svg',
                country: 'United Kingdom',
                content:
                    "This is the best app for personalizing your products. I've not found another app that comes close."
            },
            {
                store: 'Mothers Family Rings',
                avatar: 'mothers_family_rings.svg',
                country: 'United States',
                content:
                    "Since integrating Customily, we've observed a significant increase in conversion rates, and customer feedback has been overwhelmingly positive."
            },
            {
                store: 'Wunsch Kalender',
                avatar: 'wunsch_kalender.svg',
                country: 'Germany',
                content:
                    'This app is a game-changer and is saving us so many hours of work every single day. The best app for customized products by far!'
            }
        ]
    },
    Amazon: {
        title: 'Sell personalized products on Amazon and boost your revenue. Try it for FREE. Cancel anytime.',
        summary:
            'Automate fulfillment, reduce errors, and offer unlimited customization options. All from one dashboard.',
        image: 'amazon-logo.png',
        provider: 'google',
        benefits: [
            'Works with your existing Amazon listings, no coding needed',
            'Two preview options: Amazon Custom integration or post-purchase live preview link',
            'Print-ready files generated automatically with every order',
            'Free clipart & design libraries included, ready to sell from day one',
            '1-on-1 expert onboarding to get you live fast'
        ],
        reviews: [
            {
                store: 'One & Only Prints',
                avatar: 'one_and_only_prints.svg',
                country: 'United Kingdom',
                content:
                    "This is the best app for personalizing your products. I've not found another app that comes close."
            },
            {
                store: 'Mothers Family Rings',
                avatar: 'mothers_family_rings.svg',
                country: 'United States',
                content:
                    "Since integrating Customily, we've observed a significant increase in conversion rates, and customer feedback has been overwhelmingly positive."
            },
            {
                store: 'Incredible Cookies',
                avatar: 'incredible_cookies.svg',
                country: 'United States',
                content:
                    'They are the best and our business has grown far beyond our original expectations. Solid platform and an amazing support team.'
            }
        ]
    }
};
